import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Title } from "../../../../../../themes/Default/components/Title";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { List } from "../../../../../../themes/Default/components/List";
import { Header as ListHeader } from "../../../../../../themes/Default/components/List/components/Header";
import { Body as ListBody } from "../../../../../../themes/Default/components/List/components/Body";
import { Pagination } from "../../../../../../themes/Default/components/List/components/Footer/components/Pagination";
import { TableHead } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead";
import { TableBody } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody";
import { TableRow } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow";
import { TableHeadCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableHead/components/TableRow/components/TableHeadCell";
import { TableDataCell } from "../../../../../../themes/Default/components/List/components/Body/components/TableBody/components/TableDataCell";
import { Checkbox } from "../../../../../../themes/Default/components/Form/components/Checkbox";
import { useTranslation } from "react-i18next";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Toaster } from "../../../../../../themes/Default/components/Toaster";
import { legalEntityManagement } from "../../../../../../hooks/api/LegalEntitiesManagement/LegalEntitiesManagement";

const LegalEntities = () => {
    const { t } = useTranslation("common");
    const recordsPerPage = 10;
    const defaultFilters = {
        startPage: 0,
        limitPage: recordsPerPage,
    };
    const navigate = useNavigate();
    const [filters, setFilters] = useState(defaultFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [disableDelete, setDisableDelete] = useState(true);
    const [selectedLegalEntity, setSelectedLegalEntity] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [legalEntities, setLegalEntities] = useState();
    const { useLegalEntityListMutation, useDeleteLegalEntityMutation } = legalEntityManagement;
    const [legalEntityList, { isLoading }] = useLegalEntityListMutation();
    const [deleteLegalEntity] = useDeleteLegalEntityMutation();

    const tabs = [
        { title: t("locations.title"), onClick: () => gotoLocationTab() },
        { title: t("legalEntity.title"),isActive: true  },
    ];

    const gotoLocationTab = () => {
        navigate("/settings/locations/locations");
    }

    const gotoNew = () => {
        navigate("/settings/locations/legal-entities/add");
    };

    const gotoEdit = (legalEntityId) => {
        navigate("/settings/locations/legal-entities/edit/" + legalEntityId);
    };

    const onPageChange = (currentPage) => {
        setCurrentPage(currentPage);
        const newStartPage = (currentPage - 1) * recordsPerPage;
        setFilters({
            ...filters,
            startPage: newStartPage,
            limitPage: recordsPerPage,
        });
    };

    const onSelectionChange = (id) => {
        setSelectedLegalEntity((prevSelected) => {
            if (prevSelected.includes(id)) {
                return prevSelected.filter((recordId) => recordId !== id);
            } else {
                return [...prevSelected, id];
            }
        });
    };

    const onDelete = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        for (let i = 0; i < selectedLegalEntity?.length; i++) {
                            const legalEntityId = selectedLegalEntity[i];
                            deleteLegalEntity(legalEntityId).then((result) => {
                                if (result?.error) {
                                    toast.error(result.error.data.error.responseMessage);
                                } else {
                                    legalEntityList(filters).then((result) => {
                                        const legalEntities = result?.data?.records;
                                        setLegalEntities(legalEntities);
                                        setTotalRecords(result?.data?.totalRecords);
                                    });
                                    setDisableDelete(true);
                                    toast.info(result?.data?.info?.responseMessage);
                                    onSelectionChange(legalEntityId);
                                }
                            });
                        }
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    useEffect(() => {
        if (selectedLegalEntity.length > 0) {
            setDisableDelete(false);
        } else {
            setDisableDelete(true);
        }
    }, [selectedLegalEntity]);

    useEffect(() => {
        legalEntityList().then((result) => {
            setLegalEntities(result?.data?.records);
            setTotalRecords(result?.data?.totalRecords);
        });
    }, [legalEntityList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings" />
                <Button title={t("legalEntity.addNew")} icon="fa-regular fa-plus" onClick={gotoNew}></Button>
                <Button title={t("delete")} icon="fa-regular fa-trash-can" isDisabled={disableDelete} onClick={onDelete}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{`${t("legalEntity.title")} (${totalRecords})`}</Title>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("locations.title")} />
                    <Link title={t("legalEntity.title")} />
                    <Link title={t("settings.list")} isActive={true} />
                </Breadcrumps>
                {!isLoading && (
                    <>
                        <List>
                            <ListHeader>
                            </ListHeader>
                            <ListBody>
                                <TableHead>
                                    <TableRow>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("legalEntity.name")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("legalEntity.address")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell>
                                            <span>{t("legalEntity.accountNumber")}</span>
                                        </TableHeadCell>
                                        <TableHeadCell></TableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {legalEntities?.length > 0 &&
                                        legalEntities?.map((legalEntity, index) => {
                                            return (
                                                <TableRow key={legalEntity.id}>
                                                    <TableDataCell>
                                                        <Button icon="fa-regular fa-pen" onClick={() => gotoEdit(legalEntity.id)}></Button>
                                                    </TableDataCell>
                                                    <TableDataCell>
                                                        <Checkbox name="isBlockClass" value={legalEntity.id} onChange={() => onSelectionChange(legalEntity.id)} />
                                                    </TableDataCell>
                                                    <TableDataCell>{legalEntity.name}</TableDataCell>
                                                    <TableDataCell>{legalEntity.address}</TableDataCell>
                                                    <TableDataCell>{legalEntity.accountNumber}</TableDataCell>
                                                    <TableDataCell></TableDataCell>
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </ListBody>
                        </List>
                        <Pagination currentPage={currentPage} recordsPerPage={recordsPerPage} totalRecords={totalRecords} onClick={onPageChange} />
                    </>
                )}
            </Main>
        </DefaultLayout>
    );
};

export default LegalEntities;
